import { Clipboard } from '@angular/cdk/clipboard';
import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CellClickedEvent, ColDef, GetContextMenuItemsParams, GridReadyEvent, ICellRendererParams, MenuItemDef } from 'ag-grid-community';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, finalize } from 'rxjs';
import { AGTableBase } from '../../../shared/components/table/ag-table-base';
import {
  APPSCRIPTS_NUMBER_COMMENT_ISSUES,
  AppscriptsNumber,
  AppscriptsNumberCommentIssue,
  AppscriptsNumberDetails,
  AppscriptsNumbersCollection,
  AppscriptsNumbersFilter,
  AppscriptsUpdateNumber,
  AppscriptsUpdateUser,
  DeviceTokenLogsResponse,
} from '../../../shared/models/appscripts-user.model';
import { MoNumberVerificationInformation } from '../../../shared/models/mo-number-verification-information.model';
import { NetworksByMncMccSearchDto } from '../../../shared/models/network.model';
import { AppscriptsNumberActionData, AppscriptsUserRequestParams, AppscriptsUserService } from '../../../shared/services/appscripts-user.service';
import { BrowserUtils } from '../../../shared/services/browser-utils';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { DialogRef, ModalService } from '../../../shared/services/modal.service';
import { NetInfoService } from '../../../shared/services/net-info.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { UsersService } from '../../../shared/services/users.service';
import { ValidationService, Validators as Vld } from '../../../shared/services/validation.service';
import { AppscriptsUserDetails, EMPTY_APPSCRIPTS_USER_DETAILS } from './appscripts-user-details';
import { AppscriptsUserTableActionsComponent } from './appscripts-user-table-actions/appscripts-user-table-actions.component';
declare const moment: any;

@Component({
  standalone: false,
  selector: 'app-appscripts-user-table',
  templateUrl: 'appscripts-user-table.component.html',
  styleUrls: ['appscripts-user-table.component.scss'],
})
export class AppscriptsUserTableComponent extends AGTableBase implements OnInit, OnDestroy {
  @Output() changeBatch = new EventEmitter<AppscriptsNumber[]>();
  @Input() filter: AppscriptsNumbersFilter;

  @ViewChild('infoModalTpl', { read: TemplateRef, static: false }) infoModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('deleteModalTpl', { read: TemplateRef, static: false }) deleteModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('errorsModalTpl', { read: TemplateRef, static: false }) errorsModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('balanceModalTpl', { read: TemplateRef, static: false }) balanceModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('commentModalTpl', { read: TemplateRef, static: false }) commentModalTpl: TemplateRef<HTMLTemplateElement>;
  commentModalDialogRef: DialogRef;

  storageContextName = 'appscripts-numbers';
  balanceUserId;
  currentRow: AppscriptsNumber;
  numberForm: FormGroup;
  userForm: FormGroup;
  numberDetailsPhone: string;
  numberDetails: AppscriptsNumberDetails;
  numberVerificationInfo: MoNumberVerificationInformation;
  userModel: AppscriptsUpdateUser;
  currentSection = '';
  userData: AppscriptsUserDetails = { ...EMPTY_APPSCRIPTS_USER_DETAILS };
  loading = true;
  networkInfoLoading = false;
  showNumberVerificationResetButton = true;

  requestParams = new AppscriptsUserRequestParams();
  private balanceModal: DialogRef;
  batchSelectedIds = new Set<number>();
  showPingsWarnBanner = !BrowserUtils.isProd();
  MAX_ITEMS = 5000;

  phoneNumberCommentIssues = APPSCRIPTS_NUMBER_COMMENT_ISSUES;
  phoneNumberCommentForm = this.formBuilder.group({
    commentIssue: ['', Vld.compose([Vld.required])],
    comment: ['', Vld.compose([Vld.maxLength(255)])],
  });

  // ng grid
  columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'batch',
      checkboxSelection: true,
      maxWidth: 40,
      headerCheckboxSelection: true,
      pinned: 'left',
      lockPinned: true,
      lockPosition: 'left',
      lockVisible: true,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
    },
    {
      headerName: 'ID',
      field: 'id',
      flex: 0.3,
      hide: true,
      sortable: true,
      comparator: () => 0,
      initialSort: 'desc',
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        return `<span class="one-line">${params.data.id}</span>`;
      },
    },
    {
      headerName: 'User ID',
      field: 'userId',
      flex: 0.3,
      hide: false,
      sortable: true,
      headerTooltip: 'Money SMS User ID.',
      cellRenderer: (params: ICellRendererParams) => {
        return `<span class="one-line">${params.data.userId}</span>`;
      },
    },
    {
      headerName: 'Phone',
      field: 'phoneNumber',
      tooltipField: 'phoneNumber',
      flex: 0.5,
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const isBlocked = params.data.blocked ? 'text-error-r3' : '';
        return `<div class="one-line clickable"><span class="${isBlocked}">${params.data.phoneNumber}</span></div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'Email',
      field: 'email',
      flex: 0.7,
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        return `<div class="one-line"><span>${params.data.email}</span></div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'Network',
      field: 'network',
      flex: 0.8,
      cellRenderer: (params: ICellRendererParams) => {
        return this.formatNetwork(params.data);
      },
    },
    {
      headerName: 'Last active',
      field: 'lastPing',
      flex: 0.8,
      minWidth: 95,
      maxWidth: 95,
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        return this.formatDateInTwoLines(params.data.lastPing);
      },
    },
    {
      headerName: 'Balance',
      field: 'ub.balance',
      flex: 0.35,
      minWidth: 95,
      maxWidth: 95,
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const balance = params.data.balance > 0 ? params.data.balance / 100 : 0;
        return `<div class="one-line clickable"><span>${balance}</span></div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'MT SMS',
      field: 'mtSmsCount',
      flex: 0.2,
      tooltipField: 'mtSmsCount',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        return `<div class="d-flex one-line clickable">
                    <div class="${this.getIconForMTStatus(params.data)} icon me-0_8"></div>
                    <span>${params.data.mtSmsCount}</span>
                </div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'MO SMS',
      field: 'moSmsCount',
      flex: 0.2,
      tooltipField: 'moSmsCount',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        return `<div class="d-flex one-line clickable">
                    <div class="${this.getIconForMOStatus(params.data)} icon me-0_8"></div>
                    <span>${params.data.moSmsCount}</span>
                </div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'CLI',
      field: 'voiceCallCount',
      flex: 0.2,
      tooltipField: 'voiceCallCount',
      cellRenderer: (params: ICellRendererParams) => {
        return `<div class="d-flex one-line clickable">
                    <div class="${this.getIconForCLIStatus(params.data)} icon me-0_8"></div>
                    <span>${params.data.voiceCallCount}</span>
                </div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'AI',
      field: 'ai',
      flex: 0.2,
      minWidth: 95,
      maxWidth: 95,
      cellRenderer: (params: ICellRendererParams) => {
        const currentRow: AppscriptsNumber = params.data;
        const ai = currentRow.indexes.ai;
        return ai;
      },
    },
    {
      headerName: 'Health Check',
      field: 'healthCheck',
      flex: 0.2,
      tooltipField: 'phoneNumber',
      cellClass: 'd-flex justify-content-center align-items-center',
      cellRenderer: (params: ICellRendererParams) => {
        return this.getHealthCheckStatus(params.data);
      },
    },
    {
      headerName: 'Update HLR',
      field: 'updateHlr',
      hide: true,
      flex: 0.3,
      cellClass: 'justify-content-center',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const isChecked = params.data.updateHlr ? 'checked' : '';
        return `<div class="form-check form-switch d-flex justify-content-center">
                            <input type="checkbox" class="form-check-input medium" ${isChecked}/>
                        </div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'Actions',
      field: 'actions',
      maxWidth: 130,
      minWidth: 130,
      pinned: 'right',
      lockPinned: true,
      lockPosition: 'right',
      lockVisible: true,
      suppressColumnsToolPanel: false,
      suppressMenu: false,
      suppressAutoSize: true,
      tooltipField: 'actions',
      headerTooltip: 'Right click on a row to see more options.',
      headerClass: 'action-cell',
      cellClass: 'action-cell',
      cellRenderer: AppscriptsUserTableActionsComponent,
      onCellClicked: (event: CellClickedEvent) => this.onActionClick(event),
    },
  ];

  rowData!: AppscriptsNumber[];
  // ng grid

  constructor(
    public notificationService: NotificationService,
    public service: AppscriptsUserService,
    public router: Router,
    public modal: ModalService,
    private datePipe: DatePipe,
    private formBuilder: FormBuilder,
    private localStorage: LocalStorageService,
    private userService: UsersService,
    public validationService: ValidationService,
    public netInfoService: NetInfoService,
    private clipBoard: Clipboard,
    @Inject('CW_DEVICE_LOGS_URL') private cwLogsUrl: string
  ) {
    super();
    this.numberForm = formBuilder.group({
      country: [{ value: '', disabled: true }],
      mcc: ['', Vld.compose([Vld.required, Vld.digits(true), Vld.maxLength(3)])],
      mnc: ['', Vld.compose([Vld.required, Vld.digits(true), Vld.maxLength(3)])],
      originalMnc: ['', Vld.compose([Vld.digits(true), Vld.maxLength(3)])],
      provider: [{ value: '', disabled: true }],
      originalProvider: [{ value: '', disabled: true }],
      phoneNumber: ['', Vld.compose([Vld.required])],
      blocked: [''],
      updateHlr: [''],
    });

    this.userForm = formBuilder.group({
      email: ['', Vld.compose([Vld.email(true)])],
      paymentMethodName: [''],
      paymentAddress: [''],
      referrerCode: [''],
      referralCode: ['', Vld.compose([Vld.required, Vld.maxLength(255)])],
    });

    this.numberForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed()).subscribe(() => {
      this.onNumberFormMccMncChange();
    });
  }

  ngOnInit() {
    this.actionSubscription = this.service.action$.subscribe(action => {
      this.onAction(action);
    });
    this.columnChange$.pipe(debounceTime(1000)).subscribe(() => {
      const currentColumnState = this.gridApi.getColumnState();
      this.localStorage.set(`appscripts_numbers_table_state_${this.userService.authUser.id}`, currentColumnState);
    });
    this.paginationPageSize = this.localStorage.get(`appscripts_numbers_table_component_size_${this.userService.authUser.id}`) || 20;
  }

  update() {
    this.requestParams.size = this.paginationPageSize;
    this.requestParams.page = this.currentPageNumber - 1;
    this.requestParams.sort = this.sortState.length === 0 ? ['id,desc'] : this.sortState;

    if (this.filter) {
      this.requestParams.filter = this.filter;
    }

    this.loading = true;
    this.service.all(this.requestParams).subscribe({
      next: (data: AppscriptsNumbersCollection) => {
        this.rowData = data.content;
        this.totalRowsCount = data.totalElements > this.MAX_ITEMS ? this.MAX_ITEMS : data.totalElements;
        this.loading = false;
      },
      error: e => {
        this.notificationService.error({
          title: 'Appscripts numbers',
          message: 'An error occurred while loading numbers',
          service: 'APPSRIPTS',
          requestMessage: e.statusText,
          requestCode: e.status,
          ts: e.timestamp ? e.timestamp : null,
        });
        this.loading = false;
      },
    });
  }

  private getHealthCheckStatus(row: AppscriptsNumber): string {
    const isDeviceTokenValid = row.deviceToken?.length > 0;
    const isSimStateValid = row.simState === 'SIM_STATE_READY';
    const isServiceStateValid = row.serviceState === 'IN_SERVICE' || row.serviceState === 'STATE_IN_SERVICE';
    const isSimIdValid = row.simId?.length > 0;
    const isRoaming = row.roaming;
    const hasSignal = row.lastSignalStrength && row.lastSignalStrength >= 1;
    const isPendingSimIdCheck = row.waitingSimIdCheck;

    if (isDeviceTokenValid && isSimStateValid && isServiceStateValid && isSimIdValid && hasSignal && !isRoaming && !isPendingSimIdCheck) {
      return `<div class="h-100 d-flex justify-content-center align-items-center">
            <span class="far fa-smile text-success-s2 fs-2"></span>
            </div>`;
    }
    return `<div class="h-100 d-flex justify-content-center align-items-center">
            <span class="icon-attention text-accent-a2 fs-2"></span>
        </div>`;
  }

  onCellClick(event: CellClickedEvent) {
    const colId = event.column.getColId();
    this.currentRow = event.data as AppscriptsNumber;

    switch (colId) {
      case 'phoneNumber':
        this.handlePhoneNumberClick(event.data);
        break;
      case 'mtSmsCount':
        this.navigateTo('/appscripts/mt-sms', { phonenumber: event.data.phoneNumber });
        break;
      case 'moSmsCount':
        this.navigateTo('/appscripts/mo-sms', { phonenumber: event.data.phoneNumber });
        break;
      case 'voiceCallCount':
        this.navigateTo('/appscripts/cli', { phonenumber: event.data.phoneNumber });
        break;
      case 'ub.balance':
        this.openBalanceModal(event.data.userId);
        break;
    }
  }

  private getIconForMTStatus(row: AppscriptsNumber): string {
    const data = { ...row, mtOptedIn: true }; // Because mtOptedIn is always true ...
    if (!data.mtAllowed) return 'icon-status-not-allowed';
    if (data.mtAllowed && !data.mtOptedIn) return 'icon-status-allowed';
    if (data.mtAllowed && data.mtOptedIn) {
      if (data.mtEnabled) return 'icon-status-enabled';
      return 'icon-status-opted-in';
    }
  }

  private getIconForMOStatus(data: AppscriptsNumber): string {
    if (!data.moAllowed) return 'icon-status-not-allowed';
    if (data.moAllowed && !data.moOptedIn) return 'icon-status-allowed';
    if (data.moAllowed && data.moOptedIn) {
      if (data.moEnabled) return 'icon-status-enabled';
      return 'icon-status-opted-in';
    }
  }

  private getIconForCLIStatus(data: AppscriptsNumber): string {
    if (!data.cliAllowed) return 'icon-status-not-allowed';
    if (data.cliAllowed && !data.cliOptedIn) return 'icon-status-allowed';
    if (data.cliAllowed && data.cliOptedIn) {
      if (data.cliEnabled) return 'icon-status-enabled';
      return 'icon-status-opted-in';
    }
  }

  private handlePhoneNumberClick(data: AppscriptsNumber) {
    this.onInfo(data);
  }

  private navigateTo(route: string, queryParams: object) {
    this.router.navigate([route], { queryParams });
  }

  private openBalanceModal(userId: string) {
    this.balanceUserId = userId;
    this.balanceModal = this.modal.alert().dialogClass('modal-dialog medium-modal').component(this.balanceModalTpl).open();
  }

  private updateNumberWithData(data: AppscriptsNumber) {
    const updatedData: AppscriptsUpdateNumber = {
      phoneNumber: data.phoneNumber,
      mcc: data.mcc,
      mnc: data.mnc,
      originalMnc: data.originalMnc,
      mtEnabled: data.mtEnabled,
      mtAllowed: data.mtAllowed,
      moEnabled: data.moEnabled,
      moOptedIn: data.moOptedIn,
      moAllowed: data.moAllowed,
      cliEnabled: data.cliEnabled,
      cliOptedIn: data.cliOptedIn,
      cliAllowed: data.cliAllowed,
      blocked: data.blocked,
      updateHlr: data.updateHlr,
      comment: data.comment,
      commentIssue: data.commentIssue,
    };
    this.service.updateNumber(data.id, updatedData).subscribe({
      next: () => {
        this.notificationService.success('Update successful', 'Appscripts');
        this.update();
      },
      error: e => {
        this.notificationService.error({
          title: 'Appscripts numbers',
          message: 'An error occurred while updating number.',
          service: 'APPSRIPTS',
          requestMessage: e.statusText,
          requestCode: e.status,
          ts: e.timestamp ? e.timestamp : null,
        });
        this.loading = false;
      },
    });
  }

  onAfterSaveBalance() {
    this.balanceModal.close(true);
    this.update();
  }

  onAction(event: AppscriptsNumberActionData): void {
    this.currentRow = event.row;
    if (event.name === 'delete') {
      const dialogRef = this.modal.alert().size('sm').component(this.deleteModalTpl).open();
      dialogRef.result.then(result => {
        if (result) {
          this.delete(event.row);
        }
      });
    }
    if (event.name === 'info') {
      this.onInfo(event.row);
    }
    if (event.name === 'comment') {
      this.phoneNumberCommentForm.reset(
        {
          commentIssue: this.currentRow.commentIssue ?? AppscriptsNumberCommentIssue.OTHER,
          comment: this.currentRow.comment ?? '',
        },
        { emitEvent: false }
      );

      this.commentModalDialogRef = this.modal.alert().size('sm').component(this.commentModalTpl).open();
    }
    if (event.name === 'uploadLogsToFirebase') {
      this.loading = true;
      this.service.uploadLogsToFirebase(event.row.phoneNumber).subscribe({
        next: (result: DeviceTokenLogsResponse) => {
          if (result.phones.length > 1) {
            this.notificationService.success(
              'Logs uploaded. <br>Two numbers found under same device token: ' + result.phones.join(', '),
              'Appscripts'
            );
          } else {
            this.notificationService.success('Logs uploaded', 'Appscripts');
          }
          this.update();
        },
        error: e => {
          this.notificationService.error({
            title: 'Appscripts numbers',
            message: 'An error occurred while uploading logs.',
            service: 'APPSRIPTS',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ? e.timestamp : null,
          });
          this.loading = false;
        },
      });
    }

    if (event.name === 'deviceLogs') {
      const url = this.cwLogsUrl.replace('{PHONENUMBER}', event.row.phoneNumber);
      window.open(url, '_blank');
    }
  }

  public onCommentSave(): void {
    this.loading = true;
    this.currentRow.comment = this.phoneNumberCommentForm.value.comment;
    this.currentRow.commentIssue = this.phoneNumberCommentForm.value.commentIssue as AppscriptsNumberCommentIssue;
    this.updateNumberWithData(this.currentRow);
  }

  private onInfo(row: AppscriptsNumber) {
    this.numberDetailsPhone = row.phoneNumber;
    this.numberDetails = null;
    this.numberVerificationInfo = null;
    this.userModel = null;
    this.currentSection = 'appscripts-numbers-details-main';

    this.service.numberDetails(row.id).subscribe({
      next: details => (this.numberDetails = details),
      error: e => {},
    });
    this.service.getVerificationCount(row.phoneNumber).subscribe({
      next: d => (this.numberVerificationInfo = d),
      error: e => {},
    });

    this.service.one(row.id).subscribe((model: AppscriptsNumber) => {
      this.userModel = {
        id: model.userId,
        email: model.email,
        referrerCode: model.referrerCode,
        referralCode: model.referralCode,
        paymentMethodName: model.paymentMethodName,
        paymentAddress: model.paymentAddress,
      };

      this.numberForm.patchValue({
        country: model.country,
        mcc: model.mcc,
        mnc: model.mnc,
        originalMnc: model.originalMnc,
        provider: model.provider,
        originalProvider: model.originalProvider,
        phoneNumber: model.phoneNumber,
        blocked: model.blocked,
        updateHlr: model.updateHlr,
      });
      this.userData.fullName = model.fullName;
      this.userData.balance = model.balance;
      this.userData.registeredAt = model.registeredAt ? new Date(model.registeredAt) : null;
      this.userData.lastActive = model.lastPing ? new Date(model.lastPing) : null;
    });

    this.modal.alert().dialogClass('modal-dialog large-modal').component(this.infoModalTpl).open();
  }

  delete(user: AppscriptsNumber) {
    this.loading = true;
    this.service.deleteUser(user.userId).subscribe(
      () => {
        this.update();
        this.notificationService.success('User removed', 'Appscripts');
      },
      () => {
        this.loading = false;
      }
    );
  }

  formatDate(date, format = 'medium') {
    if (typeof date === 'number' && date) {
      return this.datePipe.transform(new Date(date * 1000), format);
    }

    if (typeof date === 'string' && date) {
      return this.datePipe.transform(new Date(date), format);
    }

    if (date instanceof Date) {
      return this.datePipe.transform(date, format);
    }
    return '-';
  }

  formatDateInTwoLines(item: string) {
    if (item) {
      const timestamp = moment(item);
      const now = moment();
      const isRecent = now.diff(timestamp, 'minutes') <= 5;
      const date = timestamp.format('DD/MM/YY HH:mm:ss').split(' ');
      return `
                <div class="d-flex justify-content-between align-items-center one-line">
                    <div class="two-lines">
                        <span>${date[1]} <br>${date[0]}</span>
                    </div>
                    <span title="${isRecent ? 'Online' : 'Offline'}" class="fas fa-circle ${isRecent ? 'text-success-s2' : 'text-error-r2'} position-absolute end-0 me-10"></span>
                </div>
            `;
    }
    return 'N/A';
  }

  formatNetwork(row: AppscriptsNumber) {
    let text = `${row.country}(${row.mcc})/${row.provider}(${row.mnc})`;
    if (row.originalProvider) {
      text += ` <i class="icon-ported"></i> ${row.originalProvider}(${row.originalMnc})`;
    }
    return `<span class="two-lines">${text}</span>`;
  }

  onClickReset(): void {
    this.showNumberVerificationResetButton = false;
    this.service.resetVerificationCount(this.numberVerificationInfo.phoneNumber).subscribe({
      next: () => {
        this.service.getVerificationCount(this.numberVerificationInfo.phoneNumber).subscribe(res => {
          this.notificationService.success('Verification count reset successful', 'Modems Users');
          this.numberVerificationInfo = res;
          this.showNumberVerificationResetButton = true;
        });
      },
      error: e => {
        this.notificationService.error({
          title: 'Appscripts numbers',
          message: 'An error occurred while resetting.',
          service: 'MO-TESTING',
          requestMessage: e.statusText,
          requestCode: e.status,
          ts: e.timestamp ? e.timestamp : null,
        });
        this.showNumberVerificationResetButton = true;
      },
    });
  }

  onSubmitNumber() {
    this.loading = true;
    const formRawValue = this.numberForm.getRawValue();
    const updatedData: AppscriptsUpdateNumber = { ...this.currentRow, ...formRawValue };
    this.service.updateNumber(updatedData.id, updatedData).subscribe({
      next: () => {
        this.loading = false;
        this.notificationService.success('Number updated', 'Appscripts');
      },
      error: e => {
        this.loading = false;
        this.validationService.handleRequestError(e, 'Appscripts');
      },
    });
  }

  onSubmitUser() {
    this.loading = true;
    this.service.updateUser(this.userModel.id, this.userModel).subscribe({
      next: () => {
        this.loading = false;
        this.notificationService.success('User updated', 'Appscripts');
      },
      error: e => {
        this.loading = false;
        this.validationService.handleRequestError(e, 'Appscripts');
      },
    });
  }

  scrollTo(sectionId: string) {
    document.querySelector('#' + sectionId).scrollIntoView();
    this.currentSection = sectionId;
  }

  // ng grid
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    const columnState = this.localStorage.get(`appscripts_numbers_table_state_${this.userService.authUser.id}`);
    if (columnState) {
      const idColumn = columnState.find(col => col.colId === 'id');
      if (idColumn?.sort === 'desc') {
        this.update();
      } else {
        this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
      }
    } else {
      this.update();
    }
  }

  resetBatch() {
    this.batchSelectedIds.clear();
    this.gridApi.deselectAll();
  }

  onRowSelected() {
    const selectedRows = this.gridApi.getSelectedRows();
    this.batchSelectedIds = new Set(selectedRows.map(row => row.id));
    this.changeBatch.emit(selectedRows);
  }

  onSortChange() {
    const columnState = this.gridApi.getColumnState();
    this.sortState = columnState.map(c => (c.sort ? `${c.colId},${c.sort}` : null));
    this.localStorage.set(`appscripts_numbers_table_state_${this.userService.authUser.id}`, columnState);
    this.update();
  }

  getContextMenuItems = (params: GetContextMenuItemsParams): MenuItemDef[] => {
    const row: AppscriptsNumber = params.node.data;
    const colId = params.column.getColId();

    const baseItems: MenuItemDef[] = [];

    if (!['mtSmsCount', 'moSmsCount', 'voiceCallCount'].includes(colId)) {
      baseItems.push({
        name: 'Copy',
        icon: '<span class="icon-copy fs-4"></span>',
        action: () => {
          const renderedValue = this.getRenderedCellValue(params);
          this.clipBoard.copy(renderedValue || `${row.id}`);
        },
      });
    }

    baseItems.push({
      name: `${row?.blocked ? 'Unblock' : 'Block'} number`,
      icon: '<span class="fas fa-ban fs-4"></span>',
      action: () => {
        this.currentRow = row;
        this.currentRow.blocked = !this.currentRow.blocked;
        this.updateNumberWithData(this.currentRow);
      },
    });

    const conditionalItems: MenuItemDef[] = [];
    if (colId === 'mtSmsCount') {
      conditionalItems.push(...this.getMtMenuItems(row));
    } else if (colId === 'moSmsCount') {
      conditionalItems.push(...this.getMoMenuItems(row));
    } else if (colId === 'voiceCallCount') {
      conditionalItems.push(...this.getVoiceMenuItems(row));
    }
    return [...baseItems, ...conditionalItems];
  };

  private getMtMenuItems(row: AppscriptsNumber): MenuItemDef[] {
    const items: MenuItemDef[] = [];
    const mtRow = { ...row, mtOptedIn: true };

    if (!mtRow.mtAllowed) {
      items.push({
        name: 'MT Allowed',
        icon: '<span class="icon-status-allowed" style="height:18px;width:18px;"></span>',
        action: () => {
          this.updateNumberWithData({ ...mtRow, mtAllowed: true });
        },
      });
    }

    if (mtRow.mtAllowed && !mtRow.mtOptedIn) {
      items.push({
        name: 'MT Not allowed',
        icon: '<span class="icon-status-not-allowed" style="height:18px;width:18px;"></span>',
        action: () => {
          this.updateNumberWithData({ ...mtRow, mtAllowed: false });
        },
      });
    }

    if (mtRow.mtAllowed && mtRow.mtOptedIn) {
      if (mtRow.mtEnabled) {
        items.push(
          {
            name: 'MT Not allowed',
            icon: '<span class="icon-status-not-allowed" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...mtRow, mtAllowed: false, mtEnabled: false });
            },
          },
          {
            name: 'MT Opted In / Disabled',
            icon: '<span class="icon-status-opted-in" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...mtRow, mtEnabled: false });
            },
          }
        );
      } else {
        items.push(
          {
            name: 'MT Not allowed',
            icon: '<span class="icon-status-not-allowed" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...mtRow, mtAllowed: false });
            },
          },
          {
            name: 'MT Enabled',
            icon: '<span class="icon-status-enabled" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...mtRow, mtEnabled: true });
            },
          }
        );
      }
    }

    return items;
  }

  private getMoMenuItems(row: AppscriptsNumber): MenuItemDef[] {
    const items: MenuItemDef[] = [];

    if (!row.moAllowed) {
      items.push({
        name: 'MO Allowed',
        icon: '<span class="icon-status-allowed" style="height:18px;width:18px;"></span>',
        action: () => {
          this.updateNumberWithData({ ...row, moAllowed: true });
        },
      });
    }

    if (row.moAllowed && !row.moOptedIn) {
      items.push({
        name: 'MO Not allowed',
        icon: '<span class="icon-status-not-allowed" style="height:18px;width:18px;"></span>',
        action: () => {
          this.updateNumberWithData({ ...row, moAllowed: false });
        },
      });
    }

    if (row.moAllowed && row.moOptedIn) {
      if (row.moEnabled) {
        items.push(
          {
            name: 'MO Not allowed',
            icon: '<span class="icon-status-not-allowed" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...row, moAllowed: false, moEnabled: false });
            },
          },
          {
            name: 'MO Opted In / Disabled',
            icon: '<span class="icon-status-opted-in" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...row, moEnabled: false });
            },
          }
        );
      } else {
        items.push(
          {
            name: 'MO Not allowed',
            icon: '<span class="icon-status-not-allowed" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...row, moAllowed: false });
            },
          },
          {
            name: 'MO Enabled',
            icon: '<span class="icon-status-enabled" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...row, moEnabled: true });
            },
          }
        );
      }
    }

    return items;
  }

  private getVoiceMenuItems(row: AppscriptsNumber): MenuItemDef[] {
    const items: MenuItemDef[] = [];

    if (!row.cliAllowed) {
      items.push({
        name: 'CLI Allowed',
        icon: '<span class="icon-status-allowed" style="height:18px;width:18px;"></span>',
        action: () => {
          this.updateNumberWithData({ ...row, cliAllowed: true });
        },
      });
    }

    if (row.cliAllowed && !row.cliOptedIn) {
      items.push({
        name: 'CLI Not allowed',
        icon: '<span class="icon-status-not-allowed" style="height:18px;width:18px;"></span>',
        action: () => {
          this.updateNumberWithData({ ...row, cliAllowed: false });
        },
      });
    }

    if (row.cliAllowed && row.cliOptedIn) {
      if (row.cliEnabled) {
        items.push(
          {
            name: 'CLI Not allowed',
            icon: '<span class="icon-status-not-allowed" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...row, cliAllowed: false, cliEnabled: false });
            },
          },
          {
            name: 'CLI Opted In / Disabled',
            icon: '<span class="icon-status-opted-in" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...row, cliEnabled: false });
            },
          }
        );
      } else {
        items.push(
          {
            name: 'CLI Not allowed',
            icon: '<span class="icon-status-not-allowed" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...row, cliAllowed: false });
            },
          },
          {
            name: 'CLI Enabled',
            icon: '<span class="icon-status-enabled" style="height:18px;width:18px;"></span>',
            action: () => {
              this.updateNumberWithData({ ...row, cliEnabled: true });
            },
          }
        );
      }
    }
    return items;
  }

  ngOnDestroy() {
    this.actionSubscription.unsubscribe();
  }

  private onNumberFormMccMncChange(): void {
    const mccCtrl = this.numberForm.get('mcc');
    const mncCtrl = this.numberForm.get('mnc');
    const originalMncCtrl = this.numberForm.get('originalMnc');

    if (mccCtrl.invalid || mncCtrl.invalid || originalMncCtrl.invalid) {
      return;
    }

    const searchDto: NetworksByMncMccSearchDto = [
      {
        mcc: mccCtrl.value,
        mnc: mncCtrl.value,
        originalMnc: originalMncCtrl.value,
      },
    ];

    this.networkInfoLoading = true;
    this.netInfoService
      .networksByMccMnc(searchDto)
      .pipe(
        catchError(e => {
          this.notificationService.error({
            title: 'Appscripts numbers',
            message: 'An error occurred while loading network info',
            service: 'NetInfo',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ? e.timestamp : null,
          });

          return EMPTY;
        }),
        finalize(() => (this.networkInfoLoading = false))
      )
      .subscribe(networks => {
        const network = networks[0];
        this.numberForm.patchValue(
          {
            country: network?.country?.name || '',
            provider: network?.name || '',
            originalProvider: network?.originalName || '',
          },
          { emitEvent: false }
        );
      });
  }
}
